import React, { useState } from 'react';
import { Calendar, Send, CheckCircle, XCircle, RefreshCw, FileText, Download, Eye } from 'lucide-react';
import { useApi } from '../hooks/useApi';

const Schedule = () => {
  const { fetchPost, axiosPost } = useApi();

  // Get tomorrow's date in Warsaw timezone (CET/CEST)
  const today = new Date();
  const warsawTomorrow = new Date(today.toLocaleString("en-US", {timeZone: "Europe/Warsaw"}));
  warsawTomorrow.setDate(warsawTomorrow.getDate() + 1);
  const tomorrowISO = `${warsawTomorrow.getFullYear()}-${String(warsawTomorrow.getMonth() + 1).padStart(2, '0')}-${String(warsawTomorrow.getDate()).padStart(2, '0')}`;

  const [formData, setFormData] = useState({
    date: tomorrowISO,
    file: null
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isShowingSchedule, setIsShowingSchedule] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (file) => {
    setFormData(prev => ({
      ...prev,
      file: file
    }));
  };

  const handleSubmit = async () => {
    if (!formData.file || !formData.date) {
      setStatus({ type: 'error', message: 'Please select a file and date' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: 'info', message: 'Generating schedule...' });
    setResult(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('date', formData.date);
      formDataToSend.append('file', formData.file);

      const response = await fetchPost('/generate-schedule', formDataToSend);
      const data = await response.json();

      if (data.status === 'success') {
        setResult(data);
        setStatus({ type: 'success', message: 'Schedule generated successfully!' });

        // Reset file input
        setFormData(prev => ({
          ...prev,
          file: null
        }));
      } else {
        setStatus({ type: 'error', message: data.message || 'Generation failed' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Network error. Please check your connection.' });
      console.error('Error generating schedule:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShowSchedule = async () => {
    if (!formData.date) {
      setStatus({ type: 'error', message: 'Please select a date' });
      return;
    }

    setIsShowingSchedule(true);
    setStatus({ type: 'info', message: 'Loading schedule...' });
    setResult(null);

    try {
      const response = await axiosPost('/show-schedule', {
        date: formData.date
      });

      if (response.data.status === 'success') {
        setResult({
          ...response.data,
          // No excel_content for show-schedule
          excel_content: null
        });
        setStatus(null);
      } else {
        setStatus({ type: 'error', message: response.data.message || 'Failed to load schedule' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Network error. Please check your connection.' });
      console.error('Error showing schedule:', error);
    } finally {
      setIsShowingSchedule(false);
    }
  };

  const downloadExcel = () => {
    if (!result?.excel_content) return;

    // Decode base64 and create blob
    const byteCharacters = atob(result.excel_content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    // Download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.output_filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatVolume = (value) => {
    if (!value || value === 0) return '-';
    // Round to 1 decimal and format
    const rounded = Math.round(value * 10) / 10;
    // If it's a whole number, don't show decimal
    return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
  };

  // Format volume for Total row - shows 0 instead of "-"
  const formatTotalVolume = (value) => {
    if (!value || value === 0) return '0';
    // Round to 1 decimal and format
    const rounded = Math.round(value * 10) / 10;
    // If it's a whole number, don't show decimal
    return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
  };

  const formatPosition = (value) => {
    if (value === 0) return '0';
    // Round to 1 decimal and format
    const rounded = Math.round(value * 10) / 10;
    // If it's a whole number, don't show decimal
    const formatted = rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
    if (rounded > 0) return `+${formatted}`;
    return formatted;
  };

  const getPositionClass = (value) => {
    if (value > 0) return 'text-green-600';
    if (value < 0) return 'text-red-600';
    return 'text-gray-500';
  };

  return (
    <div className="space-y-8">
      {/* Date Selection */}
      <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl">
        <label className="flex items-center space-x-3 text-lg font-semibold text-gray-700 mb-3">
          <Calendar className="w-6 h-6" />
          <span>Date of Noble transactions</span>
          <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          value={formData.date}
          onChange={(e) => {
            const newDate = e.target.value;
            if (newDate) {
              setFormData(prev => ({
                ...prev,
                date: newDate
              }));
            }
          }}
          className={`w-full px-4 py-3 border-2 ${formData.date ? 'border-green-400' : 'border-gray-300'} rounded-lg focuSell: border-gray-500 focuSell: outline-none transition-all font-mono text-lg`}
        />
      </div>

      {/* File Upload Section */}
      <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-gray-400 transition-all">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <div className="w-1 h-8 bg-gray-600 mr-3"></div>
          Noble Transactions
          <span className="ml-auto text-sm font-normal text-gray-600">
            {formData.file ? '1/1' : '0/1'} files uploaded
          </span>
        </h2>

        <div className="grid grid-cols-1 gap-4">
          <div className={`bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-all ${!formData.file ? 'border-2 border-orange-200' : ''}`}>
            <label className="block cursor-pointer">
              <div className="flex items-center space-x-3 mb-2">
                <FileText className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">Transactions File</span>
                <span className="text-red-500">*</span>
              </div>
              <input
                type="file"
                accept=".xlsx,.xls"
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-all"
                onChange={(e) => handleFileChange(e.target.files[0])}
              />
              {formData.file ? (
                <p className="mt-2 text-xs text-green-600 flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {formData.file.name}
                </p>
              ) : (
                <p className="mt-2 text-xs text-orange-600">Required</p>
              )}
            </label>
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleShowSchedule}
          disabled={isShowingSchedule || isSubmitting || !formData.date}
          className="flex-1 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
        >
          <Eye className="w-5 h-5" />
          {isShowingSchedule ? 'Loading...' : 'Show Schedule'}
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || isShowingSchedule || !formData.file || !formData.date}
          className="flex-1 py-3 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
        >
          <Send className="w-5 h-5" />
          {isSubmitting ? 'Generating...' : 'Generate Schedule'}
        </button>
      </div>

      {/* Status message */}
      {status && status.message && (
        <div className={`p-4 rounded-lg flex items-center space-x-3 ${
          status.type === 'success' ? 'bg-green-100 text-green-800' :
          status.type === 'error' ? 'bg-red-100 text-red-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {status.type === 'success' && <CheckCircle className="w-5 h-5" />}
          {status.type === 'error' && <XCircle className="w-5 h-5" />}
          {status.type === 'info' && <RefreshCw className="w-5 h-5 animate-spin" />}
          <p className="font-medium">{status.message}</p>
        </div>
      )}

      {/* Results Section */}
      {result && result.summary && (
        <div className="border-2 border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <div className="w-1 h-8 bg-purple-600 mr-3"></div>
              Position Summary for {result.date}
            </h2>
            {result.excel_content && (
              <button
                onClick={downloadExcel}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
              >
                <Download className="w-5 h-5" />
                <span>Download Schedule</span>
              </button>
            )}
          </div>

          {/* Position Summary Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b-2 border-gray-300" rowSpan="2">Hour</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700 border-b border-gray-300 bg-blue-50" colSpan="3">Fixing I</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700 border-b border-gray-300 bg-green-50" colSpan="3">Fixing II</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700 border-b border-gray-300 bg-purple-50" colSpan="3">Open Position</th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-blue-50">Vol</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-blue-50">Price</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-blue-50">Action</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-green-50">Vol</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-green-50">Avg Price</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-green-50">Action</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-purple-50">Fix I</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-purple-50">Fix II</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-purple-50">Open</th>
                </tr>
              </thead>
              <tbody>
                {result.summary.hours.map((hour, idx) => (
                  <tr key={hour.hour} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-2 font-mono font-semibold text-gray-700 border-b border-gray-100">
                      H{String(hour.hour).padStart(2, '0')}
                    </td>
                    {/* Fixing I */}
                    <td className="px-3 py-2 text-center border-b border-gray-100 bg-blue-50/30">
                      {formatVolume(hour.fix_i.volume)}
                    </td>
                    <td className="px-3 py-2 text-center border-b border-gray-100 bg-blue-50/30 font-mono">
                      {hour.fix_i.price != null ? hour.fix_i.price.toFixed(2) : '-'}
                    </td>
                    <td className={`px-3 py-2 text-center border-b border-gray-100 bg-blue-50/30 font-medium ${
                      hour.fix_i.direction === 'Buy' ? 'text-red-600' :
                      hour.fix_i.direction === 'Sell' ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {hour.fix_i.direction || '-'}
                    </td>
                    {/* Fixing II */}
                    <td className="px-3 py-2 text-center border-b border-gray-100 bg-green-50/30">
                      {formatVolume(hour.fix_ii.volume)}
                    </td>
                    <td className="px-3 py-2 text-center border-b border-gray-100 bg-green-50/30 font-mono">
                      {hour.fix_ii.avg_price != null ? hour.fix_ii.avg_price.toFixed(2) : '-'}
                    </td>
                    <td className={`px-3 py-2 text-center border-b border-gray-100 bg-green-50/30 font-medium ${
                      hour.fix_ii.direction === 'Buy' ? 'text-red-600' :
                      hour.fix_ii.direction === 'Sell' ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {hour.fix_ii.direction || '-'}
                    </td>
                    {/* Open Position */}
                    <td className={`px-3 py-2 text-center border-b border-gray-100 bg-purple-50/30 font-mono ${getPositionClass(hour.open_position.fix_i_net)}`}>
                      {formatPosition(hour.open_position.fix_i_net)}
                    </td>
                    <td className={`px-3 py-2 text-center border-b border-gray-100 bg-purple-50/30 font-mono ${getPositionClass(hour.open_position.fix_ii_net)}`}>
                      {formatPosition(hour.open_position.fix_ii_net)}
                    </td>
                    <td className={`px-3 py-2 text-center border-b border-gray-100 bg-purple-50/30 font-mono font-bold ${getPositionClass(hour.open_position.open)}`}>
                      {formatPosition(hour.open_position.open)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 font-semibold">
                  <td className="px-3 py-2 text-gray-700 border-t-2 border-gray-300">Total</td>
                  <td className="px-3 py-2 text-center border-t-2 border-gray-300 bg-blue-50" colSpan="3">
                    <span className="text-red-600">Buy: {formatTotalVolume(result.summary.hours.reduce((sum, h) => sum + (h.fix_i.direction === 'Buy' ? h.fix_i.volume : 0), 0))} MWh</span>
                    {' / '}
                    <span className="text-green-600">Sell: {formatTotalVolume(result.summary.hours.reduce((sum, h) => sum + (h.fix_i.direction === 'Sell' ? h.fix_i.volume : 0), 0))} MWh</span>
                  </td>
                  <td className="px-3 py-2 text-center border-t-2 border-gray-300 bg-green-50" colSpan="3">
                    <span className="text-red-600">Buy: {formatTotalVolume(result.summary.hours.reduce((sum, h) => sum + (h.fix_ii.direction === 'Buy' ? h.fix_ii.volume : 0), 0))} MWh</span>
                    {' / '}
                    <span className="text-green-600">Sell: {formatTotalVolume(result.summary.hours.reduce((sum, h) => sum + (h.fix_ii.direction === 'Sell' ? h.fix_ii.volume : 0), 0))} MWh</span>
                  </td>
                  <td className="px-3 py-2 text-center border-t-2 border-gray-300 bg-purple-50 font-mono" colSpan="3">
                    <span className="text-red-600">Buy: {formatTotalVolume(result.summary.hours.reduce((sum, h) => sum + (h.open_position.open > 0 ? h.open_position.open : 0), 0))} MWh</span>
                    {' / '}
                    <span className="text-green-600">Sell: {formatTotalVolume(result.summary.hours.reduce((sum, h) => sum + (h.open_position.open < 0 ? Math.abs(h.open_position.open) : 0), 0))} MWh</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedule;
